import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import {
  BulkDeleteCrmObjectsRequestInterface,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  GetMultiFieldSchemaResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFieldSchemaResponseInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
} from '@vendasta/crm';
import { Observable, of, throwError, switchMap, take } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { CrmDependencies, CrmInjectionToken } from '../../../tokens-and-interfaces';

@Injectable({ providedIn: 'root' })
export class TicketService implements AbstractCrmObjectService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = buildPlatformIntegrationsBaseUrl(inject(EnvironmentService).getEnvironment());
  private readonly config: CrmDependencies = inject(CrmInjectionToken);

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return this.http.post<ListCrmObjectsResponseInterface>(
      `${this.baseUrl}/portal/list_crm_tickets`,
      request,
      { withCredentials: true },
    );
  }

  listObjectsFilters(_request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return of({ filters: [] });
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return this.http.post<GetMultiCrmObjectResponseInterface>(
      `${this.baseUrl}/portal/get_multi_crm_tickets`,
      request,
      { withCredentials: true },
    );
  }

  listFieldOptions(_request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return of([]);
  }

  createCrmObject(_request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return throwError(() => new Error('TicketService.createCrmObject is not supported'));
  }

  deleteCrmObject(_request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return throwError(() => new Error('TicketService.deleteCrmObject is not supported'));
  }

  updateCrmObject(_request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return throwError(() => new Error('TicketService.updateCrmObject is not supported'));
  }

  validateCrmObject(_request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return throwError(() => new Error('TicketService.validateCrmObject is not supported'));
  }

  bulkDelete(_request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    return throwError(() => new Error('TicketService.bulkDelete is not supported'));
  }

  listFieldSchemas(crmObjectSubtype?: string): Observable<ListFieldSchemaResponseInterface> {
    return this.config.namespace$.pipe(
      take(1),
      switchMap((namespace) =>
        this.http.post<ListFieldSchemaResponseInterface>(
          `${this.baseUrl}/portal/list_ticket_field_schemas`,
          { namespace, crmObjectSubtype: crmObjectSubtype ?? '', pagingOptions: { pageSize: '200' } },
          { withCredentials: true },
        ),
      ),
    );
  }

  getMultiFieldSchema(_fieldIds: string[], _crmObjectSubtype?: string): Observable<GetMultiFieldSchemaResponseInterface> {
    return throwError(() => new Error('TicketService.getMultiFieldSchema is not supported'));
  }
}

function buildPlatformIntegrationsBaseUrl(env: Environment): string {
  let slug = 'demo';
  if (env === Environment.PROD) {
    slug = 'prod';
  }
  return `https://platform-integrations-${slug}.apigateway.co`;
}
